import React from 'react';
import PropTypes from 'prop-types';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import DialogConfirmOperation from '../components/DialogConfirmOperation';
import {
  MAX_MESSAGE_LENGTH,
  MESSAGE_PLACEHOLDER,
  REMINDER_STYLES,
  REMOVE_CONFIRM_DIALOG,
} from './reminderConstants';
import {
  useReminderFormStyles,
  useStyleCardStyles,
} from './reminderFormStyles';

/**
 * One selectable reminder style. It is its own component so the styles hook can
 * be called with this card's style and pull that style's colors off the theme.
 */
const StyleCard = ({ description, label, value }) => {
  const classes = useStyleCardStyles({ reminderStyle: value });

  return (
    <FormControlLabel
      value={value}
      className={classes.card}
      control={
        <Radio className={classes.radio} inputProps={{ 'aria-label': label }} />
      }
      label={
        <div>
          <Typography className={classes.label}>{label}</Typography>
          <Typography variant="body2" className={classes.description}>
            {description}
          </Typography>
        </div>
      }
    />
  );
};

StyleCard.propTypes = {
  description: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOf(REMINDER_STYLES.map((item) => item.value)).isRequired,
};

export const ReminderDescription = () => {
  const classes = useReminderFormStyles();

  return (
    <>
      <Typography className={classes.description}>
        Nota visibile <b>solo in app</b> all&apos;apertura del controllo/scheda.
      </Typography>
      <Typography className={classes.description}>
        <b>Non viene incluso nella stampa</b> e non modifica la descrizione del
        controllo.
      </Typography>
    </>
  );
};

const ReminderFormFields = ({
  onMessageChange,
  onStyleChange,
  reminderMessage,
  reminderStyle,
}) => {
  const classes = useReminderFormStyles();

  return (
    <>
      <div>
        <Typography id="reminder-style-label" className={classes.fieldLabel}>
          Stile visivo*
        </Typography>
        <RadioGroup
          aria-labelledby="reminder-style-label"
          className={classes.styleCardsWrapper}
          value={reminderStyle ?? ''}
          onChange={(event) => onStyleChange(event.target.value)}
        >
          {REMINDER_STYLES.map((item) => (
            <StyleCard
              key={item.value}
              value={item.value}
              label={item.label}
              description={item.description}
            />
          ))}
        </RadioGroup>
      </div>

      <div>
        <Typography id="reminder-message-label" className={classes.fieldLabel}>
          Messaggio*
        </Typography>
        <div className={classes.messageFieldWrapper}>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={reminderMessage}
            onChange={(event) => onMessageChange(event.target.value)}
            inputProps={{
              maxLength: MAX_MESSAGE_LENGTH,
              'aria-labelledby': 'reminder-message-label',
              'aria-describedby': 'reminder-message-counter',
            }}
            InputProps={{
              classes: { input: classes.messageInput },
            }}
            placeholder={MESSAGE_PLACEHOLDER}
          />
          <Typography
            id="reminder-message-counter"
            variant="body2"
            className={classes.counter}
          >
            {`${reminderMessage.length}/${MAX_MESSAGE_LENGTH} caratteri`}
          </Typography>
        </div>
      </div>
    </>
  );
};

ReminderFormFields.propTypes = {
  onMessageChange: PropTypes.func.isRequired,
  onStyleChange: PropTypes.func.isRequired,
  reminderMessage: PropTypes.string.isRequired,
  reminderStyle: PropTypes.oneOf([
    ...REMINDER_STYLES.map((item) => item.value),
    null,
  ]),
};

export const ReminderRemoveConfirmDialog = ({ onCancel, onConfirm, open }) => (
  <DialogConfirmOperation
    open={open}
    onCancel={onCancel}
    onConfirm={onConfirm}
    {...REMOVE_CONFIRM_DIALOG}
  />
);

ReminderRemoveConfirmDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default ReminderFormFields;
